/**
 * @zendir/ui - MessageStream Component
 *
 * Stream/notification window for displaying messages, events, or status updates.
 * Each message card has integrated status system with dual-coding (color + shape).
 *
 * AstroUXDS Compliance:
 * - Status colors per AstroUXDS status system
 * - Dual-coding (color + shape) for accessibility
 * - Clear visual hierarchy
 * - Proper contrast ratios
 *
 * @example
 * ```tsx
 * import { MessageStream } from '@zendir/ui';
 *
 * <MessageStream
 *   title="Response Status"
 *   messages={[
 *     { id: '1', text: 'Message Received', status: 'standby' },
 *     { id: '2', text: 'Confirmation Receipt', status: 'normal' },
 *     { id: '3', text: 'Failed', status: 'critical' },
 *   ]}
 * />
 * ```
 */

import React, { memo, useMemo } from "react";
import { useTheme } from "../../theme";
import { classNames, type StatusLevel } from "../../utils";
import { type AstroIconName } from "../display/AstroIcon";
import { Icon } from "../display/Icon";
import { useCompactMode } from "../../hooks";

// =============================================================================
// Types
// =============================================================================

export interface StreamMessage {
  /** Unique identifier */
  id: string;
  /** Message text/label */
  text: string;
  /** Status level for color and shape */
  status?: StatusLevel;
  /** Optional timestamp */
  timestamp?: Date | string;
  /** Optional icon override */
  icon?: AstroIconName;
  /** Optional metadata/details */
  details?: string;
  /** Child messages (for hierarchical display) */
  children?: StreamMessage[];
  /** Whether this message is highlighted/active */
  active?: boolean;
}

export interface MessageStreamProps {
  /** Stream title */
  title?: string;
  /** Subtitle or description */
  subtitle?: string;
  /** Array of messages to display */
  messages: StreamMessage[];
  /** Maximum height before scrolling */
  maxHeight?: number | string;
  /** Show timestamps */
  showTimestamps?: boolean;
  /** Time format for timestamps */
  timeFormat?: "relative" | "absolute" | "time-only";
  /** Variant: 'list' for vertical, 'tree' for hierarchical */
  variant?: "list" | "tree" | "compact";
  /** Custom className */
  className?: string;
  /** Card size */
  size?: "sm" | "md" | "lg";
  /** On message click */
  onMessageClick?: (message: StreamMessage) => void;
  /** Loading state */
  loading?: boolean;
  /** Empty state message */
  emptyMessage?: string;
  /** Show connection lines (tree variant) */
  showConnectors?: boolean;
  /** Enable collapsible compact mode - shows condensed view that expands on hover/click */
  collapsible?: boolean;
  /** Start expanded when in collapsible mode (pinned state) */
  defaultExpanded?: boolean;
  /** Callback when pin state changes in collapsible mode */
  onPinChange?: (isPinned: boolean) => void;
}

// =============================================================================
// Status Shapes (same as DataValue for consistency)
// =============================================================================

const STATUS_SHAPES: Record<
  StatusLevel,
  (size: number, color: string) => React.ReactNode
> = {
  off: (size, color) => (
    <svg viewBox="0 0 12 12" width={size} height={size} aria-hidden="true">
      <circle cx="6" cy="6" r="5" fill={color} />
    </svg>
  ),
  standby: (size, color) => (
    <svg viewBox="0 0 12 12" width={size} height={size} aria-hidden="true">
      <circle cx="6" cy="6" r="5" fill="none" stroke={color} strokeWidth="2" />
    </svg>
  ),
  normal: (size, color) => (
    <svg viewBox="0 0 12 12" width={size} height={size} aria-hidden="true">
      <circle cx="6" cy="6" r="5" fill={color} />
    </svg>
  ),
  caution: (size, color) => (
    <svg viewBox="0 0 12 12" width={size} height={size} aria-hidden="true">
      <rect x="1" y="1" width="10" height="10" fill={color} />
    </svg>
  ),
  serious: (size, color) => (
    <svg viewBox="0 0 12 12" width={size} height={size} aria-hidden="true">
      <polygon points="6,1 11,6 6,11 1,6" fill={color} />
    </svg>
  ),
  critical: (size, color) => (
    <svg viewBox="0 0 12 12" width={size} height={size} aria-hidden="true">
      <polygon points="6,11 1,2 11,2" fill={color} />
    </svg>
  ),
};

// =============================================================================
// Helper Functions
// =============================================================================

function getStatusColor(status: StatusLevel | undefined, tokens: any): string {
  switch (status) {
    case "normal":
      return tokens.colors.status.normal;
    case "standby":
      return tokens.colors.status.standby;
    case "caution":
      return tokens.colors.status.caution;
    case "serious":
      return tokens.colors.status.serious;
    case "critical":
      return tokens.colors.status.critical;
    case "off":
      return tokens.colors.status.off;
    default:
      return tokens.colors.status.standby;
  }
}

function formatTimestamp(
  timestamp: Date | string | undefined,
  format: "relative" | "absolute" | "time-only",
): string {
  if (!timestamp) return "";

  const date = typeof timestamp === "string" ? new Date(timestamp) : timestamp;

  if (format === "relative") {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (seconds < 60) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return date.toLocaleDateString();
  }

  if (format === "time-only") {
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }

  return date.toLocaleString();
}

// =============================================================================
// Message Card Component
// =============================================================================

interface MessageCardProps {
  message: StreamMessage;
  tokens: any;
  size: "sm" | "md" | "lg";
  showTimestamp: boolean;
  timeFormat: "relative" | "absolute" | "time-only";
  onClick?: (message: StreamMessage) => void;
  variant: "list" | "tree" | "compact";
}

const MessageCard = memo(function MessageCard({
  message,
  tokens,
  size,
  showTimestamp,
  timeFormat,
  onClick,
  variant,
}: MessageCardProps) {
  const status = message.status || "standby";
  const statusColor = getStatusColor(status, tokens);

  const sizeConfig = {
    sm: {
      padding: 8,
      fontSize: "0.75rem",
      fontSizeSmall: "0.625rem",
      iconSize: 10,
      gap: 6,
    }, // 12px / 10px in rem
    md: {
      padding: 12,
      fontSize: "0.875rem",
      fontSizeSmall: "0.75rem",
      iconSize: 12,
      gap: 8,
    }, // 14px / 12px in rem
    lg: {
      padding: 16,
      fontSize: "1rem",
      fontSizeSmall: "0.875rem",
      iconSize: 14,
      gap: 10,
    }, // 16px / 14px in rem
  }[size];

  const isCompact = variant === "compact";

  return (
    <div
      role="listitem"
      aria-label={`${message.text} - ${status}`}
      onClick={() => onClick?.(message)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: sizeConfig.gap,
        padding: isCompact
          ? `${sizeConfig.padding / 2}px ${sizeConfig.padding}px`
          : sizeConfig.padding,
        backgroundColor: tokens.colors.background.elevated,
        border: `1.5px solid ${statusColor}`,
        borderRadius: tokens.borderRadius.md,
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.15s ease",
        ...(message.active && {
          boxShadow: `0 0 0 2px ${statusColor}40`,
        }),
      }}
      onMouseEnter={(e) => {
        if (onClick) {
          e.currentTarget.style.backgroundColor =
            tokens.colors.background.surface;
          e.currentTarget.style.transform = "translateY(-1px)";
        }
      }}
      onMouseLeave={(e) => {
        if (onClick) {
          e.currentTarget.style.backgroundColor =
            tokens.colors.background.elevated;
          e.currentTarget.style.transform = "translateY(0)";
        }
      }}
    >
      {/* Status Shape */}
      <span style={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
        {STATUS_SHAPES[status](sizeConfig.iconSize, statusColor)}
      </span>

      {/* Message Text */}
      <span
        style={{
          flex: 1,
          fontSize: sizeConfig.fontSize,
          fontWeight: 500,
          color: tokens.colors.text.primary,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {message.text}
      </span>

      {/* Details (if any) */}
      {message.details && !isCompact && (
        <span
          style={{
            fontSize: sizeConfig.fontSizeSmall,
            color: tokens.colors.text.tertiary,
          }}
        >
          {message.details}
        </span>
      )}

      {/* Timestamp */}
      {showTimestamp && message.timestamp && (
        <span
          style={{
            fontSize: sizeConfig.fontSizeSmall,
            color: tokens.colors.text.tertiary,
            fontFamily: tokens.typography.fontFamily.mono,
            whiteSpace: "nowrap",
          }}
        >
          {formatTimestamp(message.timestamp, timeFormat)}
        </span>
      )}
    </div>
  );
});

// =============================================================================
// Tree Node Component (for hierarchical display)
// =============================================================================

interface TreeNodeProps {
  message: StreamMessage;
  tokens: any;
  size: "sm" | "md" | "lg";
  showTimestamp: boolean;
  timeFormat: "relative" | "absolute" | "time-only";
  onClick?: (message: StreamMessage) => void;
  depth: number;
  showConnectors: boolean;
  isLast: boolean;
}

const TreeNode = memo(function TreeNode({
  message,
  tokens,
  size,
  showTimestamp,
  timeFormat,
  onClick,
  depth,
  showConnectors,
  isLast,
}: TreeNodeProps) {
  const hasChildren = message.children && message.children.length > 0;
  const indentSize = 32;

  return (
    <div style={{ position: "relative" }}>
      {/* Connector Lines */}
      {showConnectors && depth > 0 && (
        <>
          {/* Vertical line from parent */}
          <div
            style={{
              position: "absolute",
              left: (depth - 1) * indentSize + 16,
              top: 0,
              width: 2,
              height: isLast ? "50%" : "100%",
              backgroundColor: tokens.colors.border.muted,
            }}
          />
          {/* Horizontal line to node */}
          <div
            style={{
              position: "absolute",
              left: (depth - 1) * indentSize + 16,
              top: "50%",
              width: indentSize - 8,
              height: 2,
              backgroundColor: tokens.colors.border.muted,
            }}
          />
        </>
      )}

      {/* Message Card */}
      <div style={{ marginLeft: depth * indentSize }}>
        <MessageCard
          message={message}
          tokens={tokens}
          size={size}
          showTimestamp={showTimestamp}
          timeFormat={timeFormat}
          onClick={onClick}
          variant="tree"
        />
      </div>

      {/* Render children */}
      {hasChildren && (
        <div style={{ marginTop: 8 }}>
          {message.children!.map((child, index) => (
            <div key={child.id} style={{ marginTop: index > 0 ? 8 : 0 }}>
              <TreeNode
                message={child}
                tokens={tokens}
                size={size}
                showTimestamp={showTimestamp}
                timeFormat={timeFormat}
                onClick={onClick}
                depth={depth + 1}
                showConnectors={showConnectors}
                isLast={index === message.children!.length - 1}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

// =============================================================================
// MessageStream Component
// =============================================================================

export const MessageStream = memo(function MessageStream({
  title,
  subtitle,
  messages,
  maxHeight,
  showTimestamps = false,
  timeFormat = "relative",
  variant = "list",
  className = "",
  size = "md",
  onMessageClick,
  loading = false,
  emptyMessage = "No messages",
  showConnectors = true,
  collapsible = false,
  defaultExpanded = false,
  onPinChange,
}: MessageStreamProps): React.ReactElement {
  const { tokens, theme, prefersReducedMotion } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";

  // Collapsible compact mode state management
  const { expanded, isPinned, handlers, transitionDuration } = useCompactMode({
    compact: collapsible,
    defaultExpanded,
    onPinChange,
  });

  // Card container styles - Use transparent background with blur for transparent themes (matching Timeline)
  const containerStyle = useMemo(
    () => ({
      backgroundColor: isTransparentTheme
        ? "transparent"
        : tokens.colors.background.surface,
      ...(isTransparentTheme && {
        backdropFilter: "blur(12px)" as const,
        WebkitBackdropFilter: "blur(12px)" as const,
      }),
      ...(tokens.colors.border.cardStyle ?? {
        border: `1px solid ${tokens.colors.border.muted}`,
      }),
      borderRadius: tokens.borderRadius.lg,
      padding: tokens.spacing.md,
      display: "flex",
      flexDirection: "column" as const,
    }),
    [tokens, isTransparentTheme],
  );

  // Count messages by status for compact view summary (must be before any conditional return for rules-of-hooks)
  const statusCounts = useMemo(() => {
    const counts = { normal: 0, caution: 0, critical: 0, other: 0 };
    messages.forEach((m) => {
      if (m.status === "normal") counts.normal++;
      else if (m.status === "caution") counts.caution++;
      else if (m.status === "critical" || m.status === "serious")
        counts.critical++;
      else counts.other++;
    });
    return counts;
  }, [messages]);

  // Loading skeleton
  if (loading) {
    return (
      <article
        className={classNames("zendir-message-stream", className)}
        style={containerStyle}
        aria-busy="true"
        aria-label="Loading messages"
      >
        {title && (
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                height: 18,
                width: 120,
                backgroundColor: tokens.colors.background.elevated,
                borderRadius: 4,
                marginBottom: 8,
              }}
            />
            {subtitle && (
              <div
                style={{
                  height: 14,
                  width: 200,
                  backgroundColor: tokens.colors.background.elevated,
                  borderRadius: 4,
                }}
              />
            )}
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              style={{
                height: 44,
                backgroundColor: tokens.colors.background.elevated,
                borderRadius: tokens.borderRadius.md,
                border: `2px solid ${tokens.colors.border.muted}`,
              }}
            />
          ))}
        </div>
      </article>
    );
  }

  // Collapsed view - shows summary of messages
  if (collapsible && !expanded) {
    return (
      <article
        className={classNames("zendir-message-stream", "collapsed", className)}
        {...handlers}
        style={{
          ...containerStyle,
          padding: `${tokens.spacing.sm} ${tokens.spacing.md}`,
          cursor: "pointer",
          transition: `all ${transitionDuration} ease-out`,
        }}
        onMouseEnter={(e) => {
          handlers.onMouseEnter();
          if (!prefersReducedMotion) {
            (e.currentTarget as HTMLElement).style.transform = "scale(1.01)";
            (e.currentTarget as HTMLElement).style.boxShadow =
              `0 4px 12px ${tokens.colors.accent.primary}20`;
          }
        }}
        onMouseLeave={(e) => {
          handlers.onMouseLeave();
          (e.currentTarget as HTMLElement).style.transform = "scale(1)";
          (e.currentTarget as HTMLElement).style.boxShadow = "";
        }}
        aria-label={`${title || "Messages"} (collapsed)`}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: tokens.spacing.md,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: tokens.spacing.sm,
            }}
          >
            <Icon
              name="message"
              size={14}
              color={tokens.colors.accent.primary}
            />
            <span style={{ fontWeight: tokens.typography.fontWeight.medium }}>
              {title ? title.split(" ")[0] : "Messages"}
            </span>
            <span
              style={{
                color: tokens.colors.text.tertiary,
                fontSize: tokens.typography.fontSize.xs,
              }}
            >
              ({messages.length})
            </span>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: tokens.spacing.sm,
            }}
          >
            {statusCounts.critical > 0 && (
              <span
                style={{
                  color: tokens.colors.status.critical,
                  fontSize: tokens.typography.fontSize.xs,
                  fontWeight: tokens.typography.fontWeight.medium,
                }}
              >
                {statusCounts.critical} critical
              </span>
            )}
            {statusCounts.caution > 0 && (
              <span
                style={{
                  color: tokens.colors.status.caution,
                  fontSize: tokens.typography.fontSize.xs,
                  fontWeight: tokens.typography.fontWeight.medium,
                }}
              >
                {statusCounts.caution} warn
              </span>
            )}
            {isPinned && (
              <Icon
                name="lock"
                size={12}
                color={tokens.colors.accent.primary}
              />
            )}
          </div>
        </div>
      </article>
    );
  }

  return (
    <div
      className={classNames(
        "zendir-message-stream",
        collapsible ? "expanded" : "",
        className,
      )}
      {...(collapsible ? handlers : {})}
      role={collapsible ? handlers.role : "log"}
      tabIndex={collapsible ? handlers.tabIndex : undefined}
      aria-expanded={collapsible ? handlers["aria-expanded"] : undefined}
      style={{
        ...containerStyle,
        cursor: collapsible ? "pointer" : undefined,
        transition: collapsible
          ? `all ${transitionDuration} ease-out`
          : undefined,
      }}
      aria-label={title || "Message stream"}
      aria-live="polite"
    >
      {/* Header */}
      {(title || subtitle) && (
        <header style={{ marginBottom: 16 }}>
          {title && (
            <h3
              style={{
                fontSize: tokens.typography.fontSize.lg,
                fontWeight: 500, // AstroUXDS medium for headers
                color: tokens.colors.text.primary,
                margin: 0,
              }}
            >
              {title}
            </h3>
          )}
          {subtitle && (
            <p
              style={{
                fontSize: tokens.typography.fontSize.sm,
                color: tokens.colors.text.tertiary,
                margin: "4px 0 0 0",
              }}
            >
              {subtitle}
            </p>
          )}
        </header>
      )}

      {/* Messages Container — tabIndex for keyboard scroll access (WCAG 2.1.1) */}
      <div
        role="list"
        tabIndex={maxHeight ? 0 : undefined}
        aria-label={
          maxHeight ? `${title || "Messages"} scrollable list` : undefined
        }
        style={{
          display: "flex",
          flexDirection: "column",
          gap: variant === "compact" ? 4 : 8,
          ...(maxHeight && {
            maxHeight,
            overflowY: "auto",
            paddingRight: 8,
          }),
        }}
      >
        {messages.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: 32,
              color: tokens.colors.text.tertiary,
              fontSize: tokens.typography.fontSize.sm,
            }}
          >
            {emptyMessage}
          </div>
        ) : variant === "tree" ? (
          messages.map((message, index) => (
            <TreeNode
              key={message.id}
              message={message}
              tokens={tokens}
              size={size}
              showTimestamp={showTimestamps}
              timeFormat={timeFormat}
              onClick={onMessageClick}
              depth={0}
              showConnectors={showConnectors}
              isLast={index === messages.length - 1}
            />
          ))
        ) : (
          messages.map((message) => (
            <MessageCard
              key={message.id}
              message={message}
              tokens={tokens}
              size={size}
              showTimestamp={showTimestamps}
              timeFormat={timeFormat}
              onClick={onMessageClick}
              variant={variant}
            />
          ))
        )}
      </div>
    </div>
  );
});

export default MessageStream;
